import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Spin, Tag, Button as AntButton, message, Modal, Tooltip } from "antd";
import { ArrowLeftOutlined, LoadingOutlined } from "@ant-design/icons";
import { Container } from "../../components/atoms/container/Container";
import { Text } from "../../components/atoms/text/Text";
import { Icon } from "../../components/icon/Icon";
import { TicketBasicDetails } from "../../components/organisms/ticketBasicDetails/TicketBasicDetails";
import {
  getAmbulanceTripById,
  getAmbulanceTripAssignmentsApi,
  getAmbulanceProviderById,
  updateServiceStatusApi,
  updateAmbulanceStatusApi,
  getDriverLocationHistoryApi,
  getTicketById,
  fetchAmbulanceById,
} from "../../http";
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { connectAmbulanceCall } from "../actions/actions";
import { ShowAlert } from "../../utils/showAlert";
import style from "../../components/organisms/ticketBasicDetWrapper/ticketdetails.module.scss";
import style1 from "../../components/organisms/ticketBasicDetails/ticketBasicDetails.module.scss";

/* ─── constants ──────────────────────────────────────────────── */

const statusConfig: Record<string, { color: string; bg: string }> = {
  Requested: { color: "#1677ff", bg: "#e6f4ff" },
  Confirmed: { color: "#fa8c16", bg: "#fff7e6" },
  Completed: { color: "#52c41a", bg: "#f6ffed" },
  Cancelled: { color: "#ff4d4f", bg: "#fff2f0" },
};

const fmt = (d?: string) =>
  d ? new Date(d).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—";

/* ─── Helper functions ────────────────────────────────────────── */

// All coordinates in this app use [lat, lng] format
// - Ticket geoLocation.coordinates: [lat, lng]
// - Ambulance trip pickup/drop: [lat, lng]
// - Driver location: [lat, lng]
// - Leaflet expects: [lat, lng] ✓ (no conversion needed)
// - OSRM expects: lng,lat in URL (need to swap when calling API)
// - Google Maps URL: lat,lng (no conversion needed)

// Decode OSRM polyline (returns [lat, lng] pairs - Leaflet ready)
const decodePolyline = (encoded: string): [number, number][] => {
  const points: [number, number][] = [];
  let index = 0, lat = 0, lng = 0;
  while (index < encoded.length) {
    let b, shift = 0, result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlat = (result & 1) ? ~(result >> 1) : (result >> 1);
    lat += dlat;
    shift = 0;
    result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlng = (result & 1) ? ~(result >> 1) : (result >> 1);
    lng += dlng;
    points.push([lat / 1e5, lng / 1e5]);
  }
  return points;
};

// Format duration (seconds) to human readable
const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  return remMins > 0 ? `${hrs}h ${remMins}m` : `${hrs}h`;
};

// Format distance (meters) to human readable
const formatDistance = (meters: number): string => {
  if (meters < 1000) return `${Math.round(meters)}m`;
  return `${(meters / 1000).toFixed(1)} km`;
};

// Fetch route from OSRM - accepts [lat, lng] coords (our format)
const fetchOSRMRoute = async (
  waypoints: number[][] // Array of [lat, lng]
): Promise<{ geometry: [number, number][]; legs: { duration: number; distance: number }[] } | null> => {
  if (waypoints.length < 2) return null;
  try {
    // OSRM expects lng,lat in URL - swap our lat,lng
    const coords = waypoints.map(([lat, lng]) => `${lng},${lat}`).join(";");
    const url = `https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=polyline`;
    const res = await fetch(url);
    const data = await res.json();
    if (data.code !== "Ok" || !data.routes?.[0]) return null;
    const route = data.routes[0];
    return {
      geometry: decodePolyline(route.geometry),
      legs: route.legs.map((leg: any) => ({ duration: leg.duration, distance: leg.distance })),
    };
  } catch {
    return null;
  }
};

/* ─── ProviderCard ────────────────────────────────────────────── */

const AMBULANCE_STEPS = [
  { key: "confirmed", label: "Confirmed", icon: "✅" },
  { key: "dispatched", label: "Dispatched", icon: "🚑" },
  { key: "arrivedPickup", label: "Arrived", icon: "📍" },
  { key: "patientPicked", label: "Picked", icon: "🙋" },
  { key: "patientDropped", label: "Dropped", icon: "🏥" },
];

// Component to fit map bounds to route
const FitBounds = ({ points }: { points: [number, number][] }) => {
  const map = useMap();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (points.length >= 2) {
      map.fitBounds(points, { padding: [30, 30] });
    }
  }, [map, points]);
  return null;
};

const ProviderCard = ({
  assignment,
  tripId,
  ticketId,
  trip,
  onRefresh,
  extraMsgFields,
}: {
  assignment: any;
  tripId: string;
  ticketId: string;
  trip: any;
  onRefresh: () => Promise<void>;
  /** Extra message fields fetched from the ticket / ambulance record — fills in
   *  Patient Condition, Requirements, Helpers, Pickup Time, and the correct POC
   *  Contact Person, which are not stored in the trip document itself. */
  extraMsgFields?: {
    patientCondition: string;
    requirement: string;
    noofhelper: string;
    pickuptime: string;
    contactPersonName: string;
    contactPersonMobile: string;
  };
}) => {
  const [provider, setProvider] = useState<any>(null);
  const [cancelling, setCancelling] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [calling, setCalling] = useState(false);
  const [updatingStep, setUpdatingStep] = useState<string | null>(null);
  const [showMap, setShowMap] = useState(false);
  const [locationHistory, setLocationHistory] = useState<any[]>([]);
  const [loadingMap, setLoadingMap] = useState(false);
  const [routeData, setRouteData] = useState<{
    geometry: [number, number][];
    legs: { duration: number; distance: number }[];
  } | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!assignment.providerId) return;
    getAmbulanceProviderById(assignment.providerId)
      .then((res) => setProvider(res.data?.data))
      .catch(() => { });
  }, [assignment.providerId]);

  const currentServiceStatus = assignment.serviceStatus?.slice(-1)[0]?.status;
  const cfg = statusConfig[currentServiceStatus];
  const primaryMobile =
    provider?.contact?.mobile?.find((m: any) => m.isPrimary) ??
    provider?.contact?.mobile?.[0];
  const mobileStr = primaryMobile
    ? `${primaryMobile.countryCode} ${primaryMobile.number}`
    : null;

  const currentStepIdx = AMBULANCE_STEPS.findIndex(
    (s) => s.key === assignment.ambulanceStatus?.slice(-1)[0]?.status
  );
  const isActive =
    currentServiceStatus !== "Cancelled" && currentServiceStatus !== "Completed";

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await Promise.all([
        updateServiceStatusApi(assignment._id, "Confirmed"),
        updateAmbulanceStatusApi(assignment._id, "confirmed"),
      ]);
      ShowAlert("success", "Assignment confirmed");
      const scrollY = window.scrollY;
      await onRefresh();
      requestAnimationFrame(() => window.scrollTo(0, scrollY));
    } catch {
      ShowAlert("error", "Failed to confirm");
    } finally {
      setConfirming(false);
    }
  };

  const handleAmbulanceStep = async (stepKey: string) => {
    setUpdatingStep(stepKey);
    try {
      const calls: Promise<any>[] = [updateAmbulanceStatusApi(assignment._id, stepKey)];
      if (stepKey === "confirmed")
        calls.push(updateServiceStatusApi(assignment._id, "Confirmed"));
      await Promise.all(calls);
      ShowAlert(
        "success",
        `Status updated to ${AMBULANCE_STEPS.find((s) => s.key === stepKey)?.label}`
      );
      const scrollY = window.scrollY;
      await onRefresh();
      requestAnimationFrame(() => window.scrollTo(0, scrollY));
    } catch {
      ShowAlert("error", "Failed to update status");
    } finally {
      setUpdatingStep(null);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm(`Cancel trip for ${assignment.providerName}?`)) return;
    setCancelling(true);
    try {
      await updateServiceStatusApi(assignment._id, "Cancelled");
      ShowAlert("success", "Trip cancelled");
      const scrollY = window.scrollY;
      await onRefresh();
      requestAnimationFrame(() => window.scrollTo(0, scrollY));
    } catch {
      ShowAlert("error", "Failed to cancel trip");
    } finally {
      setCancelling(false);
    }
  };

  const handleToggleMap = async () => {
    if (showMap) {
      setShowMap(false);
      return;
    }
    setLoadingMap(true);
    try {
      const res = await getDriverLocationHistoryApi(assignment._id);
      const history = res.data?.data ?? [];
      setLocationHistory(history);

      // Build waypoints [lat, lng] format
      const driverCoords = history.length > 0 ? history[history.length - 1].coordinates : null;
      const pickupCoords = trip?.pickupLocation?.coordinates;
      const dropCoordsList = (trip?.dropLocation ?? [])
        .map((d: any) => d.coordinates)
        .filter((c: any) => c?.length >= 2);

      const waypoints: number[][] = [];
      // If driver location exists, route from driver to drops (skip pickup - already passed)
      // If no driver, route from pickup to drops
      if (driverCoords?.length >= 2) {
        waypoints.push(driverCoords);
      } else if (pickupCoords?.length >= 2) {
        waypoints.push(pickupCoords);
      }
      waypoints.push(...dropCoordsList);

      if (waypoints.length >= 2) {
        const route = await fetchOSRMRoute(waypoints);
        setRouteData(route);
      }

      setShowMap(true);
    } catch {
      ShowAlert("error", "Failed to load location history");
    } finally {
      setLoadingMap(false);
    }
  };

  // Get coordinates [lat, lng]
  const driverCoords = locationHistory.length > 0
    ? locationHistory[locationHistory.length - 1].coordinates
    : null;
  const pickupCoords = trip?.pickupLocation?.coordinates;
  const dropLocations = (trip?.dropLocation ?? []).map((d: any, idx: number) => ({
    coords: d.coordinates,
    address: d.address,
    index: idx,
  }));

  // Driver's traveled path (grey line from first to current location)
  const traveledPath: [number, number][] = locationHistory.length > 1
    ? locationHistory
      .filter((loc: any) => loc.coordinates?.length >= 2)
      .map((loc: any) => [loc.coordinates[0], loc.coordinates[1]] as [number, number])
    : [];
  const startingCoords = locationHistory.length > 1 ? locationHistory[0].coordinates : null;

  // Calculate cumulative ETAs
  const getDropETA = (dropIndex: number): { duration: number; distance: number } | null => {
    if (!routeData?.legs) return null;
    const hasDriver = driverCoords?.length >= 2;
    const startLegIdx = hasDriver ? 1 : 0;
    let cumDuration = 0;
    let cumDistance = 0;
    for (let i = startLegIdx; i <= startLegIdx + dropIndex && i < routeData.legs.length; i++) {
      cumDuration += routeData.legs[i].duration;
      cumDistance += routeData.legs[i].distance;
    }
    return { duration: cumDuration, distance: cumDistance };
  };

  // Build points for map bounds [lat, lng] - direct use with Leaflet
  const mapPoints: [number, number][] = [];
  if (startingCoords?.length >= 2) mapPoints.push([startingCoords[0], startingCoords[1]]);
  if (driverCoords?.length >= 2) mapPoints.push([driverCoords[0], driverCoords[1]]);
  if (pickupCoords?.length >= 2) mapPoints.push([pickupCoords[0], pickupCoords[1]]);
  dropLocations.forEach((d: any) => {
    if (d.coords?.length >= 2) mapPoints.push([d.coords[0], d.coords[1]]);
  });

  const center: [number, number] = mapPoints[0] ?? [20.5937, 78.9629];

  // Build WhatsApp message for this provider
  const patientName = trip?.patient?.name ?? "N/A";
  const patientMobile = trip?.patient?.mobile ? `${trip.patient.mobile.countryCode} ${trip.patient.mobile.number}` : "N/A";
  const benId = trip?.patient?.benId ?? "N/A";
  const pickupAddress = trip?.pickupLocation?.address ?? "N/A";
  const pickupCoordinates = trip?.pickupLocation?.coordinates;
  const lastDrop = (trip?.dropLocation ?? []).slice(-1)[0];
  const dropAddress = lastDrop?.address ?? "N/A";
  const dropCoordinates = lastDrop?.coordinates;
  const pickupMapUrl = pickupCoordinates ? `https://www.google.com/maps?q=${pickupCoordinates[0]},${pickupCoordinates[1]}` : "N/A";
  const dropMapUrl = dropCoordinates ? `https://www.google.com/maps?q=${dropCoordinates[0]},${dropCoordinates[1]}` : "N/A";

  const whatsappMsg = `*Ambulance Request from Pococare*
An ambulance booking has been created for ${patientName} based on a service request.

Booking ID: ${benId}

Contact Person: ${extraMsgFields?.contactPersonName ?? patientName} | ${extraMsgFields?.contactPersonMobile ?? patientMobile}

Patient Condition: ${extraMsgFields?.patientCondition ?? "N/A"}

Ambulance Type: ${trip?.ambulanceType ?? "N/A"}

Requirements: ${extraMsgFields?.requirement ?? "N/A"}

No. of Helpers: ${extraMsgFields?.noofhelper ?? "N/A"}

Pickup Address: ${pickupAddress}

Pickup Map: ${pickupMapUrl}

Drop Address: ${dropAddress}

Drop Map: ${dropMapUrl}

Pickup Time: ${extraMsgFields?.pickuptime ?? "Immediately"}

Driver Link: ${assignment.driverLinkToken && assignment.driverLinkActive ? (assignment.driverLinkUrl ?? "N/A") : "N/A"}`;

  const handleWhatsApp = () => {
    if (!mobileStr) return;
    const phone = mobileStr.replace(/[^0-9]/g, "");
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(whatsappMsg)}`;
    window.open(url, "_blank");
  };

  return (
    <div
      style={{
        border: "1px solid #e8e8e8",
        borderRadius: 12,
        background: "#fff",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        marginBottom: 12,
        overflow: "hidden",
      }}
    >
      {/* ═══ Header ═══ */}
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid #f0f0f0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 8,
        }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: "1.3rem" }}>🚑</span>
          <span style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: "1rem", color: "#222" }}>
            {assignment.providerName ?? "—"}
          </span>
          {mobileStr && (
            <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: "0.85rem", color: "#666" }}>
              ({mobileStr})
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {currentServiceStatus && (
            <Tooltip
              title={
                <div style={{ fontFamily: "Poppins", fontSize: "0.75rem" }}>
                  <div style={{ fontWeight: 600, marginBottom: 6, borderBottom: "1px solid rgba(255,255,255,0.2)", paddingBottom: 4 }}>
                    Status History
                  </div>
                  {(assignment.serviceStatus ?? []).map((entry: any, idx: number) => (
                    <div key={idx} style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "2px 0" }}>
                      <span>{entry.status}</span>
                      <span style={{ opacity: 0.7 }}>{fmt(entry.timestamp)}</span>
                    </div>
                  ))}
                </div>
              }
              placement="bottomRight"
            >
              <Tag
                style={{
                  color: cfg?.color ?? "#555",
                  background: cfg?.bg ?? "#f5f5f5",
                  border: `1px solid ${cfg?.color ?? "#d9d9d9"}40`,
                  fontFamily: "Poppins",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  margin: 0,
                  cursor: "pointer",
                  padding: "2px 10px",
                  borderRadius: 4,
                }}>
                {currentServiceStatus}
              </Tag>
            </Tooltip>
          )}
          <span style={{ fontFamily: "Poppins", fontSize: "0.75rem", color: "#999" }}>
            {fmt(assignment.serviceStatus?.slice(-1)[0]?.timestamp)}
          </span>
        </div>
      </div>



      {/* ═══ Horizontal Stepper ═══ */}
      <div style={{ padding: "16px", borderBottom: "1px solid #f0f0f0" }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            position: "relative",
          }}>
          <div
            style={{
              position: "absolute",
              top: 12,
              left: 24,
              right: 24,
              height: 2,
              background: "#e8e8e8",
              zIndex: 0,
            }}
          />
          <div
            style={{
              position: "absolute",
              top: 12,
              left: 24,
              width: currentStepIdx >= 0
                ? `calc(${Math.min(currentStepIdx / (AMBULANCE_STEPS.length - 1), 1) * 100}% - 48px)`
                : 0,
              height: 2,
              background: "#52c41a",
              zIndex: 1,
              transition: "width 0.3s ease",
            }}
          />

          {AMBULANCE_STEPS.map((step, idx) => {
            const isDone = currentStepIdx >= idx;
            const isNext = currentStepIdx + 1 === idx;
            const clickable = isNext && !updatingStep && isActive;
            const entry = assignment.ambulanceStatus?.find((e: any) => e.status === step.key);

            return (
              <div
                key={step.key}
                onClick={() => clickable && handleAmbulanceStep(step.key)}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  flex: 1,
                  cursor: clickable ? "pointer" : "default",
                  zIndex: 2,
                  opacity: !isDone && !isNext ? 0.4 : 1,
                }}>
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    background: isDone ? "#52c41a" : isNext && isActive ? "#385BF0" : "#e8e8e8",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 6,
                    transition: "all 0.2s ease",
                    boxShadow: isNext && isActive ? "0 0 0 4px rgba(56,91,240,0.15)" : "none",
                  }}>
                  {isDone && <span style={{ color: "#fff", fontSize: "0.7rem", fontWeight: 700 }}>✓</span>}
                  {updatingStep === step.key && <span style={{ color: "#fff", fontSize: "0.6rem" }}>...</span>}
                </div>
                <span
                  style={{
                    fontFamily: "Poppins",
                    fontSize: "0.72rem",
                    fontWeight: isDone ? 600 : 500,
                    color: isDone ? "#52c41a" : isNext && isActive ? "#385BF0" : "#999",
                    textAlign: "center",
                  }}>
                  {step.label}
                </span>
                {isDone && entry?.timestamp && (
                  <span
                    style={{
                      fontFamily: "Poppins",
                      fontSize: "0.65rem",
                      color: "#999",
                      marginTop: 2,
                    }}
                  >
                    {new Date(entry.timestamp).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ═══ Action Bar ═══ */}
      <div
        style={{
          padding: "10px 16px",
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          background: "#fafafa",
        }}>
        {mobileStr && (
          <>
            <button
              onClick={async () => {
                if (calling) return;
                setCalling(true);
                try {
                  await connectAmbulanceCall({ customerNumber: mobileStr.replace(/\s/g, "") }, ticketId);
                  ShowAlert("success", "Call initiated successfully");
                } catch {
                  ShowAlert("error", "Failed to initiate call");
                } finally {
                  setCalling(false);
                }
              }}
              disabled={calling}
              style={{
                background: calling ? "#ccc" : "#385BF0",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                padding: "6px 16px",
                cursor: calling ? "not-allowed" : "pointer",
                fontWeight: 600,
                fontSize: "0.8rem",
                fontFamily: "Poppins",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              {calling ? (
                <>
                  <LoadingOutlined style={{ fontSize: 12 }} spin />
                  Calling…
                </>
              ) : (
                "📞 Call"
              )}
            </button>
            <button
              onClick={handleWhatsApp}
              style={{
                background: "#25D366",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                padding: "6px 16px",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "0.8rem",
                fontFamily: "Poppins",
              }}
            >
              💬 Send WhatsApp
            </button>
            <div style={{ width: 1, height: 24, background: "#ddd", margin: "0 4px", alignSelf: "center" }} />
          </>
        )}

        {currentServiceStatus === "Requested" && (
          <button
            onClick={handleConfirm}
            disabled={confirming}
            style={{
              background: confirming ? "#ccc" : "#52c41a",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "6px 16px",
              cursor: confirming ? "not-allowed" : "pointer",
              fontWeight: 600,
              fontSize: "0.8rem",
              fontFamily: "Poppins",
            }}
          >
            {confirming ? "..." : "✓ Confirm"}
          </button>
        )}
        {isActive && (
          <button
            onClick={handleCancel}
            disabled={cancelling}
            style={{
              background: "transparent",
              color: cancelling ? "#ccc" : "#ff4d4f",
              border: `1px solid ${cancelling ? "#ccc" : "#ff4d4f"}`,
              borderRadius: 6,
              padding: "5px 16px",
              cursor: cancelling ? "not-allowed" : "pointer",
              fontWeight: 600,
              fontSize: "0.8rem",
              fontFamily: "Poppins",
            }}
          >
            {cancelling ? "..." : "✕ Cancel"}
          </button>
        )}
        <button
          onClick={handleToggleMap}
          disabled={loadingMap}
          style={{
            background: showMap ? "#1890ff" : "transparent",
            color: showMap ? "#fff" : "#1890ff",
            border: "1px solid #1890ff",
            borderRadius: 6,
            padding: "5px 16px",
            cursor: loadingMap ? "not-allowed" : "pointer",
            fontWeight: 600,
            fontSize: "0.8rem",
            fontFamily: "Poppins",
            marginLeft: "auto",
          }}>
          {loadingMap ? "Loading..." : showMap ? "📍 Hide Map" : "📍 Track"}
        </button>
      </div>

      {/* ═══ Expandable Map ═══ */}
      {showMap && (
        <div style={{ height: 350, borderTop: "1px solid #f0f0f0", position: "relative" }}>
          <MapContainer center={center} zoom={13} style={{ height: "100%", width: "100%" }}>
            <TileLayer
              attribution="&copy; OpenStreetMap"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds points={mapPoints} />

            {/* Driver's traveled path (grey line) */}
            {traveledPath.length > 1 && (
              <Polyline
                positions={traveledPath}
                color="#888"
                weight={4}
                opacity={0.6}
              />
            )}

            {/* Starting position (grey ambulance) */}
            {startingCoords?.length >= 2 && (
              <Marker
                position={[startingCoords[0], startingCoords[1]]}
                icon={L.divIcon({
                  className: "",
                  html: '<div style="background:#888;width:28px;height:28px;border-radius:50%;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center"><div style="font-size:14px">🚑</div></div>',
                  iconSize: [28, 28],
                  iconAnchor: [14, 14],
                })}
              />
            )}

            {/* Driver's traveled path (grey line) */}
            {traveledPath.length > 1 && (
              <Polyline
                positions={traveledPath}
                color="#888"
                weight={4}
                opacity={0.6}
              />
            )}

            {/* Starting position (grey ambulance) */}
            {startingCoords?.length >= 2 && (
              <Marker
                position={[startingCoords[0], startingCoords[1]]}
                icon={L.divIcon({
                  className: "",
                  html: '<div style="background:#888;width:28px;height:28px;border-radius:50%;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center"><div style="font-size:14px">🚑</div></div>',
                  iconSize: [28, 28],
                  iconAnchor: [14, 14],
                })}
              />
            )}

            {/* Route polyline */}
            {routeData?.geometry && (
              <Polyline
                positions={routeData.geometry}
                color="#385BF0"
                weight={4}
                opacity={0.8}
                dashArray="10, 6"
              />
            )}

            {/* Driver current location */}
            {driverCoords?.length >= 2 && (
              <Marker
                position={[driverCoords[0], driverCoords[1]]}
                icon={L.divIcon({
                  className: "",
                  html: '<div style="background:#385BF0;width:32px;height:32px;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 8px rgba(56,91,240,0.5);display:flex;align-items:center;justify-content:center"><div style="font-size:18px">🚑</div></div>',
                  iconSize: [32, 32],
                  iconAnchor: [16, 16],
                })}
              />
            )}

            {/* Pickup marker */}
            {pickupCoords?.length >= 2 && (
              <Marker
                position={[pickupCoords[0], pickupCoords[1]]}
                icon={L.divIcon({
                  className: "",
                  html: '<div style="background:#52c41a;width:32px;height:32px;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:bold;color:#fff">P</div>',
                  iconSize: [32, 32],
                  iconAnchor: [16, 16],
                })}
              />
            )}

            {/* Drop markers with ETA */}
            {dropLocations.map((drop: any, idx: number) => {
              if (!drop.coords?.length) return null;
              const eta = getDropETA(idx);
              const etaText = eta ? `${formatDuration(eta.duration)} • ${formatDistance(eta.distance)}` : "";
              const label = dropLocations.length > 1 ? `D${idx + 1}` : "D";
              return (
                <Marker
                  key={idx}
                  position={[drop.coords[0], drop.coords[1]]}
                  icon={L.divIcon({
                    className: "",
                    html: `<div style="display:flex;flex-direction:column;align-items:center">
                      <div style="background:#ff4d4f;min-width:32px;height:32px;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:bold;color:#fff">${label}</div>
                      ${eta ? `<div style="background:rgba(0,0,0,0.75);color:#fff;padding:2px 6px;border-radius:4px;font-size:10px;font-family:Poppins;margin-top:4px;white-space:nowrap">${etaText}</div>` : ""}
                    </div>`,
                    iconSize: [100, 60],
                    iconAnchor: [50, 16],
                  })}
                />
              );
            })}
          </MapContainer>

          {/* Legend */}
          <div
            style={{
              position: "absolute",
              bottom: 8,
              left: 8,
              background: "rgba(255,255,255,0.95)",
              padding: "6px 10px",
              borderRadius: 6,
              fontSize: "0.7rem",
              fontFamily: "Poppins",
              display: "flex",
              gap: 12,
              boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
            }}
          >
            <span>
              <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: "#52c41a", marginRight: 4 }}></span>
              Pickup
            </span>
            <span>
              <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: "#ff4d4f", marginRight: 4 }}></span>
              Drop
            </span>
            {driverCoords?.length >= 2 && (
              <span>
                <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: "#385BF0", marginRight: 4 }}></span>
                Driver
              </span>
            )}
            {routeData && (
              <span>
                <span style={{ display: "inline-block", width: 16, height: 3, background: "#385BF0", marginRight: 4, verticalAlign: "middle" }}></span>
                Route
              </span>
            )}
            {traveledPath.length > 1 && (
              <span>
                <span style={{ display: "inline-block", width: 16, height: 3, background: "#888", marginRight: 4, verticalAlign: "middle" }}></span>
                Traveled
              </span>
            )}
          </div>
        </div>
      )}

      {/* ═══ Footer ═══ */}
      {assignment.updatedBy?.agentName && (
        <div
          style={{
            padding: "6px 16px",
            borderTop: "1px solid #f0f0f0",
            fontFamily: "Poppins",
            fontSize: "0.7rem",
            color: "#999",
            textAlign: "right",
          }}>
          Last updated by <span style={{ color: "#666" }}>{assignment.updatedBy.agentName}</span>
        </div>
      )}
    </div>
  );
};

/* ─── AmbulanceTripDetailPage ─────────────────────────────────── */

export const AmbulanceTripDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [trip, setTrip] = useState<any>(null);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isMsgModalOpen, setIsMsgModalOpen] = useState(false);
  const [showGeneral, setShowGeneral] = useState(true);
  const [showProviders, setShowProviders] = useState(true);
  // Extra data fetched from ticket + ambulance service to enrich ambulance messages
  const [ticketData, setTicketData] = useState<any>(null);
  const [ambulanceRecord, setAmbulanceRecord] = useState<any>(null);

  const fetchData = async (silent = false) => {
    if (!id) return;
    // When called as a background refresh (e.g. after a step update), skip the
    // full-page loading spinner so child component state (like showMap) is preserved.
    if (!silent) setLoading(true);
    try {
      const [tripRes, assignRes] = await Promise.all([
        getAmbulanceTripById(id),
        getAmbulanceTripAssignmentsApi(id),
      ]);
      setTrip(tripRes.data?.data);
      setAssignments(assignRes.data?.data ?? []);
    } catch {
      if (!silent) setTrip(null);
    } finally {
      setLoading(false);
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    fetchData();
  }, [id]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Once the trip loads, fetch the parent ticket and ambulance record to fill in
  // fields that are not stored in the trip document (patient condition, requirements,
  // helpers, pickup time).
  useEffect(() => {
    const tkId = trip?.ticketId;
    if (!tkId) return;
    Promise.all([
      getTicketById(tkId).catch(() => null),
      fetchAmbulanceById(tkId).catch(() => null),
    ]).then(([ticketRes, ambRes]) => {
      // Ticket service returns the ticket object as response.data (or response.data.data)
      const ticket = ticketRes?.data?.data ?? ticketRes?.data ?? null;
      setTicketData(ticket);
      // Ambulance endpoint returns an array at the root of response.data
      const ambArray = Array.isArray(ambRes?.data) ? ambRes?.data : (ambRes?.data?.data ?? []);
      setAmbulanceRecord(ambArray[0] ?? null);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip?.ticketId]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!trip) {
    return (
      <div style={{ padding: 24 }}>
        <AntButton icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>Back</AntButton>
        <div style={{ textAlign: "center", marginTop: 60, color: "#bbb" }}>Trip not found.</div>
      </div>
    );
  }

  const ticketId = trip.ticketId ?? id ?? "—";

  // Ambulance message - all coords are [lat, lng]
  const _benId = trip.patient?.benId ?? "N/A";
  const _beneficiary = trip.patient?.name ?? "N/A";
  // Include countryCode — it is stored on trip.patient.mobile
  const _patientMobile = trip.patient?.mobile
    ? `${trip.patient.mobile.countryCode ?? ""} ${trip.patient.mobile.number}`.trim()
    : "N/A";
  const _pickupCoords = trip.pickupLocation?.coordinates;
  const _lastDrop = (trip.dropLocation ?? []).slice(-1)[0];
  const _dropCoords = _lastDrop?.coordinates;
  const _pickupMap = _pickupCoords ? `https://www.google.com/maps?q=${_pickupCoords[0]},${_pickupCoords[1]}` : "N/A";
  const _dropMap = _dropCoords ? `https://www.google.com/maps?q=${_dropCoords[0]},${_dropCoords[1]}` : "N/A";

  // Contact Person: use POC from ticket data (same as primary message) — fall back to
  // trip.patient.name + trip.patient.mobile if ticket hasn't loaded yet
  const _contactPersonName = ticketData?.poc?.fullName || trip.patient?.name || "N/A";
  const _contactPersonMobile = ticketData?.poc?.mobile || _patientMobile;

  // Fields fetched from the ticket / ambulance record (may be null while loading)
  const _patientCondition = (() => {
    const cond = ticketData?.currentMedicalStatusResponse;
    if (!cond) return "N/A";
    const parts = [
      ...(cond.currentCondition ?? []),
      ...(cond.otherDescription ? [cond.otherDescription] : []),
    ];
    return parts.length > 0 ? parts.join(", ") : "N/A";
  })();

  const _assistanceReq = ambulanceRecord?.typeOfAssistanceRequired;
  const _assistanceItems: string[] = [];
  if (_assistanceReq?.wheelChair) _assistanceItems.push("wheelChair");
  if (_assistanceReq?.stretcher) _assistanceItems.push("stretcher");
  if (_assistanceReq?.paramedic) _assistanceItems.push("paramedic");
  if (_assistanceReq?.additionalHelp) _assistanceItems.push("additionalHelp");
  if (_assistanceReq?.DoctorOnBoard) _assistanceItems.push("DoctorOnBoard");
  const _requirement = _assistanceItems.length > 0 ? _assistanceItems.join(", ") : "N/A";
  const _noofhelper = _assistanceReq?.additionalHelp
    ? String(_assistanceReq?.numberofHelper ?? "N/A")
    : "N/A";

  const _pickuptime = (() => {
    const pt = (trip as any)?.scheduledPickupTime || ambulanceRecord?.pickupTime;
    if (!pt) return "Immediately";
    return new Date(pt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" });
  })();

  // Shared extra fields passed to ProviderCard WhatsApp messages
  const _extraMsgFields = {
    patientCondition: _patientCondition,
    requirement: _requirement,
    noofhelper: _noofhelper,
    pickuptime: _pickuptime,
    contactPersonName: _contactPersonName,
    contactPersonMobile: _contactPersonMobile,
  };

  const _msgFields = [
    { label: "Booking ID", value: _benId },
    { label: "Contact Person", value: `${_contactPersonName} | ${_contactPersonMobile}` },
    { label: "Patient Condition", value: _patientCondition },
    { label: "Ambulance Type", value: trip.ambulanceType ?? "N/A" },
    { label: "Requirements", value: _requirement },
    { label: "No. of Helpers", value: _noofhelper },
    { label: "Pickup Address", value: trip.pickupLocation?.address ?? "N/A" },
    { label: "Pickup Map", value: _pickupMap },
    { label: "Drop Address", value: _lastDrop?.address ?? "N/A" },
    { label: "Drop Map", value: _dropMap },
    { label: "Pickup Time", value: _pickuptime },
  ];
  const _subHeader = `An ambulance booking has been created for ${_beneficiary} based on a service request.`;
  const copyMsg =
    `*Ambulance Request from Pococare*\n${_subHeader}\n\n` +
    _msgFields.map((f) => `${f.label}: ${f.value}`).join("\n\n");

  // General Info
  const generalDrops = (trip.dropLocation ?? []).map((d: any, i: number) => ({
    label: (trip.dropLocation?.length ?? 0) > 1 ? `Drop ${i + 1}` : "Drop",
    value: d.address ?? "—",
  }));
  const generalLeft = [
    { label: "Ticket ID", value: ticketId },
    { label: "Ambulance Type", value: trip.ambulanceType ?? "—" },
    { label: "Pickup", value: trip.pickupLocation?.address ?? "—" },
    ...generalDrops,
  ];
  const generalRight = [
    { label: "Patient", value: trip.patient?.name ?? "—" },
    {
      label: "Patient Mobile",
      value: trip.patient?.mobile
        ? `${trip.patient.mobile.countryCode} ${trip.patient.mobile.number}`
        : "—",
    },
    { label: "Ben ID", value: trip.patient?.benId ?? "—" },
    { label: "Created", value: fmt(trip.createdAt) },
  ];

  return (
    <div>
      {/* Ambulance Message Modal */}
      <Modal
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingRight: 32 }}>
            <span>🚑 Ambulance Message</span>
            <button
              onClick={() => { navigator.clipboard.writeText(copyMsg); setIsMsgModalOpen(false); message.success("Copied"); }}
              style={{ background: "#00A7B5", color: "#fff", border: "none", borderRadius: 8, padding: "6px 16px", cursor: "pointer", fontWeight: 600, fontSize: 14 }}
            >
              📋 Copy & Close
            </button>
          </div>
        }
        open={isMsgModalOpen}
        onCancel={() => setIsMsgModalOpen(false)}
        width={680}
        footer={
          <button
            onClick={() => { navigator.clipboard.writeText(copyMsg); setIsMsgModalOpen(false); message.success("Copied"); }}
            style={{ background: "#00A7B5", color: "#fff", border: "none", borderRadius: 8, padding: "8px 20px", cursor: "pointer", fontWeight: 600, fontSize: 14, width: "100%" }}
          >
            📋 Copy & Close
          </button>
        }
      >
        <div style={{ fontWeight: 700, fontSize: "15px", color: "#00A7B5", marginBottom: "6px" }}>
          Ambulance Request from Pococare
        </div>
        <div style={{ fontSize: "13px", color: "#444", marginBottom: "12px", lineHeight: "1.5" }}>
          {_subHeader}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "6px", padding: "4px 0" }}>
          {_msgFields.map(({ label, value }) => (
            <div
              key={label}
              style={{
                display: "grid", gridTemplateColumns: "130px 1fr", gap: "8px",
                padding: "6px 10px", background: "#f9f9f9", borderRadius: "6px",
                borderLeft: "3px solid #00A7B5", alignItems: "center",
              }}
            >
              <div style={{ fontSize: "11px", color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                {label}
              </div>
              <div style={{ fontSize: "13px", color: "#222", wordBreak: "break-word", lineHeight: "1.4" }}>
                {value}
              </div>
            </div>
          ))}
        </div>
      </Modal>

      {/* Page title */}
      <div className={style.title1}><span>Ticket: {ticketId}</span></div>

      {/* Action bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "0 1.25rem 1rem", flexWrap: "wrap" }}>
        <AntButton icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>Back</AntButton>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => setIsMsgModalOpen(true)}
          style={{ background: "#00A7B5", color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", cursor: "pointer", fontWeight: 600, fontSize: "1rem", fontFamily: "Poppins, sans-serif" }}
        >
          🚑 Ambulance Message
        </button>
      </div>

      {/* Section 1: General Information */}
      <Container className={style.wrapper}>
        <div onClick={() => setShowGeneral(!showGeneral)} className={style1.head_flex}>
          <Text className={`${style1.title} ${style1.color}`} text="General Information" />
          <div><Icon src={showGeneral ? "/images/chevron-up.png" : "/images/chevron-down.png"} alt="chevron" /></div>
        </div>
        <div onClick={() => setShowGeneral(!showGeneral)} className={style1.border} />
      </Container>
      {showGeneral && (
        <Container className={style.ticketDet__container}>
          <TicketBasicDetails title="" leftSideListData={generalLeft} rightSideListData={generalRight} />
        </Container>
      )}

      {/* Section 2: Providers */}
      <Container className={style.wrapper}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginRight: "1rem" }}>
          <div onClick={() => setShowProviders(!showProviders)} className={style1.head_flex} style={{ flex: 1 }}>
            <Text className={`${style1.title} ${style1.color}`} text={`Providers (${assignments.length})`} />
            <div><Icon src={showProviders ? "/images/chevron-up.png" : "/images/chevron-down.png"} alt="chevron" /></div>
          </div>
          <button
            onClick={() =>
              navigate(`/ambulance-trips/${id}/select-providers`, {
                state: { trip, assignedProviderIds: assignments.map((a: any) => a.providerId?.toString()) },
              })
            }
            style={{
              background: "#385BF0", color: "#fff", border: "none", borderRadius: 8,
              padding: "7px 16px", cursor: "pointer", fontWeight: 600,
              fontSize: "0.85rem", fontFamily: "Poppins", whiteSpace: "nowrap",
            }}
          >
            🚑 Select Providers
          </button>
        </div>
        <div onClick={() => setShowProviders(!showProviders)} className={style1.border} />
      </Container>
      {showProviders && (
        <Container className={style.ticketDet__container}>
          {assignments.length === 0 ? (
            <div style={{ color: "#bbb", fontFamily: "Poppins", padding: "1rem 0" }}>
              No providers assigned yet.
            </div>
          ) : (
            assignments.map((a: any) => (
              <ProviderCard
                key={a._id}
                assignment={a}
                tripId={id!}
                ticketId={ticketId}
                trip={trip}
                onRefresh={() => fetchData(true)}
                extraMsgFields={_extraMsgFields}
              />
            ))
          )}
        </Container>
      )}
    </div>
  );
};
